// The type templates of the Secrets page (docs/spike-secrets.md "The secret, as the user
// sees it"): each one fixes the credential `kind`, the shape of the value, the scope facet
// the broker enforces (crates/ocs-broker/src/domain/mint.rs) and whether the secret carries
// an HTTP injection rule (D45). Everything here is pure: the page reads the form, these
// functions turn it into the API's documents. Labels are getters over the active language's
// messages (D70), so a template read after a language change speaks the new one.
import type { Schemas } from "../api/client";
import { messages } from "../i18n";
import { DATABASE_FIELDS, fieldNames, JIRA_SHAPE, single, type ValueShape, VPN_PROFILE_SHAPE } from "./fields";

export {
  type DatabaseField,
  type FieldName,
  type FieldSpec,
  fieldNames,
  type JiraField,
  plaintext,
  siteOrigin,
  type ValueShape,
  valueShapeOf,
} from "./fields";

export type Credential = Schemas["CredentialDto"];
export type CreateCredential = Schemas["CreateCredential"];

/** The scope keys the broker checks. */
export type Facet = "repos" | "projects" | "channels" | "databases";

export interface ScopeSpec {
  facet: Facet;
  readonly label: string;
  placeholder: string;
}

export type TemplateId =
  | "github"
  | "anthropic_api_key"
  | "claude_subscription"
  | "jira"
  | "asana"
  | "cursor_api_key"
  | "slack"
  | "database"
  | "http_api_key"
  | "vpn_profile"
  | "other";

export interface Template {
  id: TemplateId;
  readonly label: string;
  kind: string;
  namePlaceholder: string;
  value: ValueShape;
  scope?: ScopeSpec;
  /** Asks for allowed hosts and a header template (`injection`). */
  injection: boolean;
}

const copy = () => messages().secrets.templates;

export const DEFAULT_HEADER = "Authorization: Bearer {value}";

interface Spec {
  id: TemplateId;
  kind: string;
  namePlaceholder: string;
  scope?: { facet: Facet; placeholder: string };
  injection?: boolean;
}

// Cursor is declared for workflows that name it (D67); runs refuse it until it is supported.
const SPECS: Spec[] = [
  { id: "github", kind: "github_pat", namePlaceholder: "GITHUB_TOKEN", scope: { facet: "repos", placeholder: "acme/payments, acme/web" } },
  { id: "anthropic_api_key", kind: "anthropic_api_key", namePlaceholder: "ANTHROPIC_API_KEY" },
  { id: "claude_subscription", kind: "anthropic_oauth_token", namePlaceholder: "CLAUDE_CODE_OAUTH_TOKEN" },
  { id: "jira", kind: "jira_token", namePlaceholder: "JIRA_TOKEN", scope: { facet: "projects", placeholder: "PAY, OPS" } },
  { id: "asana", kind: "asana_token", namePlaceholder: "ASANA_TOKEN" },
  { id: "cursor_api_key", kind: "cursor_api_key", namePlaceholder: "CURSOR_API_KEY" },
  { id: "slack", kind: "slack_bot", namePlaceholder: "SLACK_BOT_TOKEN", scope: { facet: "channels", placeholder: "#deploys, #alerts" } },
  { id: "database", kind: "db", namePlaceholder: "prod-db", scope: { facet: "databases", placeholder: "payments_ro" } },
  { id: "http_api_key", kind: "generic", namePlaceholder: "stripe-test", injection: true },
  { id: "vpn_profile", kind: "vpn_profile", namePlaceholder: "aws-vpn" },
  { id: "other", kind: "generic", namePlaceholder: "NPM_TOKEN" },
];

function build({ id, kind, namePlaceholder, scope, injection = false }: Spec): Template {
  return {
    id,
    kind,
    namePlaceholder,
    injection,
    get label() {
      return copy().label[id];
    },
    value: valueShape(id),
    scope: scope && {
      ...scope,
      get label() {
        return copy().scope[scope.facet];
      },
    },
  };
}

function valueShape(id: TemplateId): ValueShape {
  if (id === "database") return { kind: "named", fields: DATABASE_FIELDS };
  if (id === "jira") return JIRA_SHAPE;
  if (id === "vpn_profile") return VPN_PROFILE_SHAPE;
  return single(() => copy().value[id]);
}

export const TEMPLATES: readonly Template[] = SPECS.map(build);

export function template(id: TemplateId): Template {
  const found = TEMPLATES.find((t) => t.id === id);
  if (!found) throw new Error(`unknown secret template ${id}`);
  return found;
}

/** The template a stored credential was made from, as near as its kind and injection tell. */
export function templateOf(credential: Pick<Credential, "kind" | "injection">): Template | undefined {
  if (credential.kind === "generic") return template(credential.injection ? "http_api_key" : "other");
  return TEMPLATES.find((t) => t.kind === credential.kind);
}

/** Comma- or space-separated list input to its items. */
export function listItems(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

/** What the user typed besides the value. */
export interface SecretDraft {
  name: string;
  template: Template;
  scope: string;
  hosts: string;
  header: string;
  workerExposed: boolean;
}

/** The `CreateCredential` document for a draft and its sealed value. */
export function createPayload(draft: SecretDraft, sealedValue: string): CreateCredential {
  const t = draft.template;
  const scope = t.scope ? listItems(draft.scope) : [];
  return {
    name: draft.name.trim(),
    kind: t.kind,
    sealed_value: sealedValue,
    scope: t.scope && scope.length > 0 ? { [t.scope.facet]: scope } : null,
    fields: fieldNames(t.value),
    holder: "server",
    injection: t.injection
      ? { hosts: listItems(draft.hosts).map((h) => h.toLowerCase()), header: draft.header.trim() }
      : null,
    worker_exposed: draft.workerExposed,
  };
}

/** A one-line summary of where a stored secret may be used. */
export function scopeSummary(credential: Pick<Credential, "scope" | "injection">): string {
  if (credential.injection) return credential.injection.hosts.join(", ");
  const scope = credential.scope;
  if (!scope || typeof scope !== "object") return "—";
  const parts = Object.entries(scope as Record<string, unknown>)
    .filter((e): e is [string, string[]] => Array.isArray(e[1]))
    .map(([facet, items]) => `${facet}: ${items.join(", ")}`);
  return parts.length > 0 ? parts.join("; ") : "—";
}
